import { useEffect, useRef, useState } from 'react'
import { useAuthStore } from '@/store/auth'
import {
  useProfile, useUpdateProfile, useUploadAvatar, useRemoveAvatar,
  useChangeEmail, useSendPasswordReset, AVATAR_TYPES,
} from '@/hooks/useProfile'
import { Avatar } from '@/components/ui/Avatar'

/**
 * Your profile.
 *
 * Split out of Settings because Settings is about the publication: places,
 * topics, seeds, the plan. This screen is about the person, and the two get
 * changed at different times by different people on a team account.
 *
 * Each block saves on its own. A single Save at the bottom would mean an email
 * change, which needs confirming from an inbox, sharing a button with a name
 * change, which does not.
 */

type Note = { kind: 'ok' | 'error'; text: string } | null

export function Profile() {
  const authUser = useAuthStore((s) => s.user)
  const client = useAuthStore((s) => s.client)
  const { data: profile, isLoading } = useProfile()

  const updateProfile = useUpdateProfile()
  const uploadAvatar = useUploadAvatar()
  const removeAvatar = useRemoveAvatar()
  const changeEmail = useChangeEmail()
  const sendReset = useSendPasswordReset()

  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [email, setEmail] = useState('')
  const [nameNote, setNameNote] = useState<Note>(null)
  const [photoNote, setPhotoNote] = useState<Note>(null)
  const [emailNote, setEmailNote] = useState<Note>(null)
  const [passNote, setPassNote] = useState<Note>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  // Seed the fields once the row is in, without stamping over typing.
  useEffect(() => {
    if (!profile) return
    setFirst(profile.first_name ?? '')
    setLast(profile.last_name ?? '')
  }, [profile?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setEmail(authUser?.email ?? '')
  }, [authUser?.email])

  const dirtyName =
    first.trim() !== (profile?.first_name ?? '') || last.trim() !== (profile?.last_name ?? '')
  const dirtyEmail = email.trim().toLowerCase() !== (authUser?.email ?? '').toLowerCase()

  function saveName(e: React.FormEvent) {
    e.preventDefault()
    setNameNote(null)
    updateProfile.mutate(
      { first_name: first.trim() || null, last_name: last.trim() || null },
      {
        onSuccess: () => setNameNote({ kind: 'ok', text: 'Saved.' }),
        onError: (err) =>
          setNameNote({ kind: 'error', text: (err as Error).message || 'Could not save that.' }),
      }
    )
  }

  function pickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Clear immediately, or picking the same file twice does nothing.
    e.target.value = ''
    if (!file) return
    setPhotoNote(null)
    uploadAvatar.mutate(file, {
      onSuccess: () => setPhotoNote({ kind: 'ok', text: 'Photo updated.' }),
      onError: (err) =>
        setPhotoNote({ kind: 'error', text: (err as Error).message || 'That upload failed.' }),
    })
  }

  function saveEmail(e: React.FormEvent) {
    e.preventDefault()
    setEmailNote(null)
    changeEmail.mutate(email.trim(), {
      onSuccess: () =>
        setEmailNote({
          kind: 'ok',
          text: `Check ${email.trim()} for a confirmation link. The address changes once you click it.`,
        }),
      onError: (err) =>
        setEmailNote({ kind: 'error', text: (err as Error).message || 'Could not change that.' }),
    })
  }

  function resetPassword() {
    setPassNote(null)
    const target = authUser?.email
    if (!target) return
    sendReset.mutate(target, {
      onSuccess: () =>
        setPassNote({ kind: 'ok', text: `A reset link is on its way to ${target}.` }),
      onError: (err) =>
        setPassNote({ kind: 'error', text: (err as Error).message || 'Could not send that.' }),
    })
  }

  if (isLoading) {
    return (
      <div className="settings-page" aria-busy="true">
        <h1>Profile</h1>
        <p className="settings-hint">Loading your details.</p>
      </div>
    )
  }

  return (
    <div className="settings-page profile">
      <h1>Profile</h1>

      {/* ---- photo ---- */}
      <div className="settings-section">
        <h2>Photo</h2>
        <p className="settings-hint">
          Shown beside your name in the sidebar. Up to 2 MB, and square works best.
        </p>

        <div className="profile-photo">
          <Avatar
            url={profile?.avatar_url}
            name={profile?.full_name}
            email={authUser?.email}
            size={84}
            className="avatar--lg"
          />
          <div className="profile-photo__actions">
            <input
              ref={fileRef}
              type="file"
              accept={AVATAR_TYPES.join(',')}
              onChange={pickPhoto}
              className="visually-hidden"
              aria-label="Choose a profile photo"
            />
            <button
              className="btn-secondary"
              onClick={() => fileRef.current?.click()}
              disabled={uploadAvatar.isPending}
            >
              {uploadAvatar.isPending ? 'Uploading' : profile?.avatar_url ? 'Change photo' : 'Upload photo'}
            </button>
            {profile?.avatar_url && (
              <button
                className="btn-secondary"
                onClick={() => removeAvatar.mutate()}
                disabled={removeAvatar.isPending}
              >
                Remove
              </button>
            )}
          </div>
        </div>
        {photoNote && <p className={noteClass(photoNote)}>{photoNote.text}</p>}
      </div>

      {/* ---- name ---- */}
      <form className="settings-section" onSubmit={saveName}>
        <h2>Name</h2>
        <div className="field-pair">
          <label className="field">
            <span className="field__label">First name</span>
            <input value={first} onChange={(e) => setFirst(e.target.value)} autoComplete="given-name" />
          </label>
          <label className="field">
            <span className="field__label">Last name</span>
            <input value={last} onChange={(e) => setLast(e.target.value)} autoComplete="family-name" />
          </label>
        </div>
        <div className="setting-actions">
          <button className="btn-primary" disabled={!dirtyName || updateProfile.isPending}>
            {updateProfile.isPending ? 'Saving' : 'Save name'}
          </button>
        </div>
        {nameNote && <p className={noteClass(nameNote)}>{nameNote.text}</p>}
      </form>

      {/* ---- email ---- */}
      <form className="settings-section" onSubmit={saveEmail}>
        <h2>Email</h2>
        <p className="settings-hint">
          This is what you sign in with. Changing it sends a confirmation to the new
          address, and nothing moves until you click it.
        </p>
        <label className="field">
          <span className="field__label">Email address</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        </label>
        <div className="setting-actions">
          <button className="btn-primary" disabled={!dirtyEmail || changeEmail.isPending}>
            {changeEmail.isPending ? 'Sending' : 'Change email'}
          </button>
        </div>
        {emailNote && <p className={noteClass(emailNote)}>{emailNote.text}</p>}
      </form>

      {/* ---- password ---- */}
      <div className="settings-section">
        <h2>Password</h2>
        <p className="settings-hint">
          We send a link rather than asking for the old password here, so a signed in
          session on a shared machine cannot be used to lock you out.
        </p>
        <div className="setting-actions">
          <button className="btn-secondary" onClick={resetPassword} disabled={sendReset.isPending}>
            {sendReset.isPending ? 'Sending' : 'Send a reset link'}
          </button>
        </div>
        {passNote && <p className={noteClass(passNote)}>{passNote.text}</p>}
      </div>

      {/* ---- what is not editable here ---- */}
      <div className="settings-section">
        <h2>Publication</h2>
        <p className="settings-hint">
          Your account covers {client?.display_name ?? 'this publication'}
          {client?.city_state ? ` in ${client.city_state}` : ''}. Places and topics are
          changed under Settings, since they belong to everyone on the account rather
          than to you.
        </p>
      </div>
    </div>
  )
}

function noteClass(note: NonNullable<Note>): string {
  return note.kind === 'ok' ? 'settings-hint profile-note' : 'settings-error'
}
